import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import type { OrganizationCompanyFormState } from '@/features/organization-settings/organization-company-form-state'
import { LOCAL_INPUT_TO_STATE } from '@/features/organization-settings/organization-company-form-state'
import {
  useSupplierOrganizationMutations,
  useSupplierOrganizationQuery,
} from '@/features/suppliers/hooks/use-supplier-organizations'
import {
  applySupplierOrganizationCountryChange,
  buildSupplierOrganizationFormState,
  buildUpdatePayload,
  type SupplierOrganizationFormState,
} from '@/features/suppliers/lib/supplier-organization-form-state'
import {
  isSupplierOrganizationNameTooLong,
  isSupplierOrganizationNameValid,
} from '@/features/suppliers/lib/supplier-organization-name'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { useSession } from '@/shared/hooks/useSession'

type SupplierOrganizationFormContextValue = {
  supplierOrganizationId: string
  state: SupplierOrganizationFormState
  addressState: OrganizationCompanyFormState
  countryName: string
  doesRequireLocalAddress: boolean
  latinErrorMessage: string
  canEdit: boolean
  isSaving: boolean
  isSaveDisabled: boolean
  saveDisabledHint?: string
  nameError?: string
  onIdentityChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onSingleCountryAddressInput: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onCnEnglishAddressInput: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onCnLocalAddressInput: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onContactChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onCountryChange: (countryCode: string) => void
  onSaveChanges: () => Promise<void>
}

const SupplierOrganizationFormContext =
  createContext<SupplierOrganizationFormContextValue | null>(null)

export function SupplierOrganizationFormProvider({
  children,
}: {
  children: ReactNode
}) {
  const { t } = useTranslation(['installations', 'common'])
  const { t: tCountryName } = useTranslation('countriesByIsoAlpha2')
  const { installationId, supplierOrganizationId } = useParams<{
    installationId: string
    supplierOrganizationId: string
  }>()

  const { session } = useSession()
  const { checkPermission } = usePermissions()
  const canEdit = checkPermission('organization', 'update')
  const supplierQuery = useSupplierOrganizationQuery(undefined, supplierOrganizationId)
  const { updateSupplierOrganization } = useSupplierOrganizationMutations()

  const [state, setState] = useState<SupplierOrganizationFormState | null>(null)
  const [isFormPristine, setIsFormPristine] = useState(true)

  useEffect(() => {
    if (supplierQuery.data) {
      const initial = buildSupplierOrganizationFormState(supplierQuery.data)
      if (!initial.country.trim()) {
        initial.country =
          session.organization.country ??
          session.organization.placeOfRegistration ??
          ''
      }
      setState(initial)
      setIsFormPristine(true)
    }
  }, [supplierQuery.data, session.organization.country, session.organization.placeOfRegistration])

  const doesRequireLocalAddress = useDoesOrganizationRequiresLocalAddress(
    state?.country
  )

  const latinErrorMessage = t('common:errors.latinCharacters')

  const markDirty = useCallback(() => setIsFormPristine(false), [])

  const onIdentityChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => (prev ? { ...prev, [id]: value } : prev))
      markDirty()
    },
    [markDirty]
  )

  const onSingleCountryAddressInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => {
        if (!prev) {
          return prev
        }
        return {
          ...prev,
          [id]: value,
          localStreet: id === 'street' ? value : prev.localStreet,
          localStreetNumber: id === 'streetNumber' ? value : prev.localStreetNumber,
          localSubDivision: id === 'subDivision' ? value : prev.localSubDivision,
          localPoBox: id === 'poBox' ? value : prev.localPoBox,
          localCity: id === 'city' ? value : prev.localCity,
          localPostCode: id === 'postCode' ? value : prev.localPostCode,
          localStreet2: id === 'street2' ? value : prev.localStreet2,
        }
      })
      markDirty()
    },
    [markDirty]
  )

  const onCnEnglishAddressInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => (prev ? { ...prev, [id]: value } : prev))
      markDirty()
    },
    [markDirty]
  )

  const onCnLocalAddressInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      const baseId = id.startsWith('local-') ? id.replace(/^local-/, '') : id
      const key = LOCAL_INPUT_TO_STATE[baseId]
      if (!key) {
        return
      }
      setState((prev) => (prev ? { ...prev, [key]: value } : prev))
      markDirty()
    },
    [markDirty]
  )

  const onContactChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => (prev ? { ...prev, [id]: value } : prev))
      markDirty()
    },
    [markDirty]
  )

  const onCountryChange = useCallback(
    (countryCode: string) => {
      setState((prev) => (prev ? applySupplierOrganizationCountryChange(prev, countryCode) : prev))
      markDirty()
    },
    [markDirty]
  )

  const onSaveChanges = useCallback(async () => {
    if (!state || !supplierOrganizationId) {
      return
    }
    try {
      await updateSupplierOrganization.mutateAsync({
        supplierOrganizationId,
        payload: buildUpdatePayload(state),
      })
      toast.success(t('installations:suppliers.edit.notifications.saveSuccess'))
      setIsFormPristine(true)
    } catch {
      toast.error(t('installations:suppliers.edit.notifications.saveError'))
    }
  }, [state, supplierOrganizationId, updateSupplierOrganization, t])

  const nameTooLong = state ? isSupplierOrganizationNameTooLong(state.name) : false
  const isNameValid = state ? isSupplierOrganizationNameValid(state.name) : false
  const nameError = nameTooLong
    ? t('installations:suppliers.nameMaxLengthError')
    : undefined
  const isSaveDisabled = !isNameValid || isFormPristine
  const saveDisabledHint =
    isFormPristine || isNameValid
      ? undefined
      : t('installations:suppliers.edit.saveDisabledHint.name')

  const countryCodeLower = (state?.country ?? '').trim().toLowerCase()
  const countryName =
    countryCodeLower.length === 2 ? tCountryName(countryCodeLower) : (state?.country ?? '')

  const value = useMemo((): SupplierOrganizationFormContextValue | null => {
    if (!supplierOrganizationId || !state) {
      return null
    }

    return {
      supplierOrganizationId,
      state,
      addressState: state as OrganizationCompanyFormState,
      countryName,
      doesRequireLocalAddress,
      latinErrorMessage,
      canEdit,
      isSaving: updateSupplierOrganization.isPending,
      isSaveDisabled,
      saveDisabledHint,
      nameError,
      onIdentityChange,
      onSingleCountryAddressInput,
      onCnEnglishAddressInput,
      onCnLocalAddressInput,
      onContactChange,
      onCountryChange,
      onSaveChanges,
    }
  }, [
    supplierOrganizationId,
    state,
    countryName,
    doesRequireLocalAddress,
    latinErrorMessage,
    canEdit,
    updateSupplierOrganization.isPending,
    isSaveDisabled,
    saveDisabledHint,
    nameError,
    onIdentityChange,
    onSingleCountryAddressInput,
    onCnEnglishAddressInput,
    onCnLocalAddressInput,
    onContactChange,
    onCountryChange,
    onSaveChanges,
  ])

  const suppliersPath = `/dashboard/${installationId}/suppliers`

  if (supplierQuery.isLoading || !state) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (supplierQuery.isError || !value) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <p className="text-muted-foreground">{t('installations:suppliers.edit.loadError')}</p>
        <Button type="button" variant="outline" asChild>
          <Link to={suppliersPath}>
            <ArrowLeft className="size-4" />
            {t('installations:suppliers.edit.backToList')}
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <SupplierOrganizationFormContext.Provider value={value}>
      {children}
    </SupplierOrganizationFormContext.Provider>
  )
}

export function useSupplierOrganizationForm() {
  const context = useContext(SupplierOrganizationFormContext)
  if (!context) {
    throw new Error(
      'useSupplierOrganizationForm must be used within SupplierOrganizationFormProvider'
    )
  }
  return context
}
